import React, { useRef, useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import '../styles/Navbar.css'
import logo from '../assets/TraQR_logo.png'
import 'bootstrap-icons/font/bootstrap-icons.css'
import PropTypes from 'prop-types'
import { MdArrowBackIos } from "react-icons/md";
import { useAuth } from 'react-oidc-context';

const mainMenuConfig = {
  admin: [
    { to: '/users', label: 'Users', testId: undefined, },
    { to: '/plans', label: 'Plans', testId: undefined, },
    { to: '/subscriptions', label: "Subscriptions", testId: undefined },
    { to: '/activity_logs', label: "Activity Logs", testId: undefined }
  ],

  customer: [
    { to: '/campaigns', label: 'Campaigns', testId: 'campaigns', },
    { to: '/contacts', label: 'Contacts', testId: 'contacts', },
    { to: '/proof_templates', label: ' Proof Templates', testId: 'proof_templates', },
    { to: '/print_collaterals', label: 'Print Collaterals', testId: 'print_collaterals', },
    { to: '/dashboard', label: 'Dashboard', testId: 'dashboard', }
  ],
};

const userMenuConfig = {
  admin: [
    { to: '/user_profile', label: 'User Profile', testId: 'profile' },

  ],
  customer: [
    { to: '/user_profile', label: 'User Profile', testId: 'user_profile' },
    { to: '/my_activity', label: 'My Activity', testId: 'activity_logs' },
    { to: '/subscription', label: 'Subscription', testId: 'subscriptions' },
    { to: '/payments', label: 'Payments', testId: 'payments' },
    { to: '/add_ons', label: 'Add-Ons', testId: 'add_ons' },
    { to: 'usage_tracker', label: 'Usage Tracker', testId: 'usage_tracker' }
  ]
}

const Navbar = ({ userRole }) => {
  const navbarCollapseRef = useRef(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [userName, setUserName] = useState(null);

  const auth = useAuth();

  useEffect(() => {
    const userName = auth?.user?.profile?.name;
    setUserName(userName)
  }, [])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavClick = () => {
    const toggler = document.querySelector(".navbar-toggler");
    if (isMobile && toggler?.getAttribute("aria-expanded") === "true") {
      toggler.click();
    }
    setShowProfileMenu(false);
  };

  const toggleProfileMenu = () => {
    const toggler = document.querySelector(".navbar-toggler");
    if (isMobile && toggler?.getAttribute("aria-expanded") === "true") {
      toggler.click();
    }

    setShowProfileMenu((prev) => !prev);
  };

  const handleHamburgerClick = () => {
    if (showProfileMenu) setShowProfileMenu(false);
  };

  const renderMainMenuLinks = (links) =>
    links.map((link) => (
      <li className="nav-item" key={link.to}>
        <NavLink
          to={link.to}
          className={({ isActive }) =>
            isActive
              ? "nav-link custom-nav-link active"
              : "nav-link custom-nav-link"
          }
          onClick={handleNavClick}
        >
          <span className="underline-on-active">{link.label}</span>
        </NavLink>
      </li>
    ));

  const renderUserMenuLinks = (links) =>
    links.map((link) => (
      <li key={link.to}>
        <NavLink
          to={link.to}
          className={({ isActive }) =>
            isActive
              ? "dropdown-item active"
              : "dropdown-item"
          }
          onClick={handleNavClick}
          data-testid={link.testId}
        >
          {link.label}
        </NavLink>
      </li>
    ));

  const signOutRedirect = () => {
    sessionStorage.removeItem('login_code')
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID
    const logoutUri = `${process.env.REACT_APP_COGNITO_REDIRECT_URL}/logout`
    const cognitoDomain = process.env.REACT_APP_COGNITO_CUSTOM_DOMAIN

    const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri,
    )}`

    window.location.replace(logoutUrl);
  }

  return (
    <nav className="navbar navbar-expand-lg fixed-top bg-white shadow-sm border-bottom custom-nav">
      <NavLink className="navbar-brand ms-3" to="/">
        <img src={logo} width="100" height="auto" alt="TraQR Logo" />
      </NavLink>

      <div className="d-flex align-items-center ms-auto me-2">
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNavDropdown"
          aria-controls="navbarNavDropdown"
          aria-expanded="false"
          aria-label="Toggle navigation"
          onClick={handleHamburgerClick}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {isMobile && (
          <button
            className="border-0 bg-transparent d-flex align-items-center gap-1 me-2"
            onClick={toggleProfileMenu}
          >
            <div className='profile-avatar'>
              {userName ? userName.charAt(0).toUpperCase() : "U"}
            </div>

          </button>
        )}
      </div>

      {/* Main menu */}
      <div
        className="collapse navbar-collapse"
        id="navbarNavDropdown"
        ref={navbarCollapseRef}
      >
        <ul className="navbar-nav ms-3 ms-lg-auto me-2">
          {userRole === "admin"
            ? renderMainMenuLinks(mainMenuConfig.admin)
            : (
              <>
                {renderMainMenuLinks(mainMenuConfig.customer)}
                <li className="nav-item">
                  <a
                    href={process.env.REACT_APP_TRAQR_SUPPORT_PAGE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nav-link custom-nav-link"
                    onClick={handleNavClick}
                  >
                    Support <i className="bi bi-box-arrow-up-right ms-1"></i>
                  </a>
                </li>
              </>
            )}

          {/* Profile icon only for larger screens */}
          {!isMobile && (
            <li className="nav-item custom-avatar"
            >
              <button
                className="nav-link custom-nav-link border-0 bg-transparent d-flex align-items-center gap-1 custom-avatar"
                onClick={toggleProfileMenu}
              >
                <div className='profile-avatar'>
                  {userName ? userName.charAt(0).toUpperCase() : "U"}
                </div>
              </button>
            </li>
          )}
        </ul>
      </div>

      {/* Overlay to close profile menu when clicking outside */}
      {showProfileMenu &&
        <div
          className={`profile-overlay ${showProfileMenu ? "show" : ""}`}
          onClick={() => setShowProfileMenu(false)}></div>}

      {/* Profile menu */}
      {showProfileMenu && (
        <ul className={`profile-dropdown-menu ${showProfileMenu ? "open" : ""}`}>
          {!isMobile && (
            <li className="profile-header">
              <button className="btn btn-link p-0" onClick={() => setShowProfileMenu(false)}>
                <MdArrowBackIos size={20} />
              </button>
            </li>
          )}
          {userRole === "admin"
            ? renderUserMenuLinks(userMenuConfig.admin)
            : renderUserMenuLinks(userMenuConfig.customer)
          }
          <li>
            <button
              className="btn btn-danger"
              onClick={() => {
                signOutRedirect();
                setShowProfileMenu(false);
              }}
            >
              Sign Out
            </button>
          </li>

        </ul>

      )}
    </nav>
  );
};


Navbar.propTypes = {
  userRole: PropTypes.string,
};

export default Navbar;
